import React from 'react';
import CompanyCell from '../../components/cells/CompanyCell';
import TextCell from '../../components/cells/TextCell';
import DateWithDaysDiffCell from '../../components/dataGridCells/DateWithDaysDiffCell';
import { formatMoneyWithSign } from '../../helpers';
import formatDecimal from '../../helpers/formatters';

const renderText = (value) => (
  <TextCell text={String(value)} variant="small_2" fontWeight={500} color="gray.A700" />
);

const DETAIL_SUMMARY_COLUMNS = {
  receiver: {
    header: 'Deudor',
    render: (invoice) => <CompanyCell company={invoice.receiver} showDetails={false} />,
  },
  amount: {
    header: 'Monto',
    render: (invoice) => renderText(formatMoneyWithSign(invoice.amountWithIva)),
  },
  monthlyRate: {
    header: 'Tasa operación',
    render: (invoice) => renderText(formatDecimal(invoice.offer?.monthlyRate)),
  },
  defaultRate: {
    header: 'Tasa mora',
    render: (invoice) => renderText(formatDecimal(invoice.offer?.defaultRate)),
  },
  retention: {
    header: 'Monto retenido',
    render: (invoice) => renderText(
      formatMoneyWithSign(invoice.paymentDiscounts?.retentionAssociated),
    ),
  },
  operationDays: {
    header: 'Días financiados',
    render: (invoice) => renderText(invoice.operationDays ?? '-'),
  },
  dateToPay: {
    header: 'Vencimiento',
    render: (invoice) => (
      <DateWithDaysDiffCell date={invoice.dateToPay} status={invoice.status} useColors />
    ),
  },
};

const SUMMARY_CARDS = {
  'current-invoices': (summary) => ({
    label: 'Facturas vigentes',
    count: summary?.currentCount,
    value: summary?.currentAmount,
  }),
  'accumulated-debt': (summary) => ({
    label: 'Mora acumulada',
    count: summary?.accumulatedDebtCount,
    value: summary?.accumulatedDebtAmount,
    isWarning: true,
    tooltipText: 'Intereses moratorios acumulados de tus facturas vencidas',
  }),
  'total-owed': (summary) => ({
    label: 'Total adeudado',
    count: summary?.totalOwedCount,
    value: summary?.totalOwedAmount,
    isWarning: true,
    tooltipText: 'Capital pendiente más intereses moratorios de tus facturas con deuda',
  }),
  'historic-invoices': (summary) => ({
    label: 'Facturas históricas',
    count: summary?.historicCount,
    value: summary?.historicAmount,
  }),
  'available-surplus': (summary) => ({
    label: 'Excedentes disponibles',
    value: summary?.availableSurplus,
    isWarning: Boolean(summary?.cantWithdrawSurplus),
    tooltipText: 'Monto se actualiza al recaudar pago de facturas',
  }),
};

const AMOUNT_CARDS = {
  'outstanding-capital': (invoice) => ({
    label: 'Capital adeudado',
    value: invoice.outstandingCapital,
    isWarning: false,
  }),
  'accumulated-default-interest': (invoice) => ({
    label: 'Intereses moratorios',
    value: invoice.accumulatedDefaultInterest,
    isWarning: Number(invoice.accumulatedDefaultInterest?.amount) > 0,
  }),
  'total-collected': (invoice) => ({
    label: 'Total cancelado',
    value: invoice.totalCollectedAmount,
    isWarning: false,
  }),
};

export const pickSummaryColumns = (keys) => keys
  .filter((key) => DETAIL_SUMMARY_COLUMNS[key])
  .map((key) => ({ key, ...DETAIL_SUMMARY_COLUMNS[key] }));

export const summaryCardsGetter = (keys) => (summary) => keys
  .filter((key) => SUMMARY_CARDS[key])
  .map((key) => ({ key, ...SUMMARY_CARDS[key](summary) }));

export const amountCardsGetter = (keys) => (invoice) => keys
  .filter((key) => AMOUNT_CARDS[key])
  .map((key) => ({ key, ...AMOUNT_CARDS[key](invoice) }));
